import { changeStyleClass, type TElementSpecifier, toJQuery } from "@de-xima/xima-common-js-dom";
import {
  assertNever,
  isObject,
  padStart,
  parseJsonArray,
  parseJsonObject,
  parseNumber,
  parseString,
} from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import type { TSeverity, TSeverityId } from "./Header.ts";
import { findWidgetForId } from "./Util.ts";

let NotificationInstance: NotificationHistory | undefined;

/**
 * @returns Array of JSON serialized messages.
 */
function getSessionMessages(): Record<string, unknown>[] {
  const json = $(".session-messages").text();
  try {
    const items = parseJsonArray(json);
    const messages = [] as Record<string, unknown>[];
    for (const item of items) {
      if (isObject(item)) {
        messages.push(item as Record<string, unknown>);
      }
    }
    return messages;
  } catch (e) {
    console.warn("Failed to parse session messages", e);
    return [];
  }
}

/**
 * Sets the text on the element and adds the class `fc-nh-empty`
 * if the text is empty.
 * @param $element Element on which to set text.
 * @param text Text to set.
 * @param interpolator Optional transformer for the text.
 */
function setText($element: JQuery, text: string, interpolator?: (input: string) => string) {
  const interpolatedText = interpolator !== undefined ? interpolator(text) : text;
  $element.text(interpolatedText);
  if (interpolatedText === undefined || interpolatedText.length === 0) {
    $element.addClass("fc-nh-empty");
  }
}

/**
 * @param id ID of the severity level.
 * @returns Name of the severity level.
 */
function idToSeverity(id: number): TSeverity {
  if (isSeverityId(id)) {
    return IdToSeverity[id];
  }
  return "error";
}

/**
 * @param id An ID to check.
 * @returns Whether the ID is a valid severity ID.
 */
function isSeverityId(id: number): id is TSeverityId {
  return id in IdToSeverity;
}

/**
 * @param severity Name of a severity level.
 * @returns The severity enum item.
 */
export function stringToSeverity(severity: string): TSeverity {
  const lower = String(severity).toLowerCase();
  switch (lower) {
    case "error":
    case "info":
    case "fatal":
    case "warn":
      return lower;
    default:
      return "error";
  }
}

export const SeverityToId: Record<TSeverity, TSeverityId> = {
  error: 1,
  fatal: 0,
  info: 3,
  warn: 2,
};

export const IdToSeverity: Record<TSeverityId, TSeverity> = {
  0: "fatal",
  1: "error",
  2: "warn",
  3: "info",
};

export class Notification {
  readonly detail: string;

  readonly origin: string;

  readonly severity: TSeverityId;

  readonly summary: string;

  readonly time: number;

  /**
   * @param summary Summary of this notification.
   * @param Details of this notification.
   * @param severity The severity of this notification, one of constants defined in `Severity`.
   * @param time Timestamp when this message was issued.
   * @param origin Origin where this message came from / was issued.
   */
  constructor(summary: string, detail: string, severity: TSeverityId, origin: string, time: number) {
    this.detail = detail;
    this.summary = summary;
    this.severity = severity;
    this.time = time;
    this.origin = origin;
  }

  /**
   * @param $template Template for the notification.
   * @returns The rendered HTML element for this notification.
   */
  render($template: JQuery): JQuery {
    const $html = $template.clone(false, false);
    if ($html.length === 0) {
      return $();
    }
    let severityClass: string = "";
    if (this.severity === SeverityToId.fatal) {
      severityClass = parseString($template.data("iconFatal"));
    } else if (this.severity === SeverityToId.error) {
      severityClass = parseString($template.data("iconError"));
    } else if (this.severity === SeverityToId.warn) {
      severityClass = parseString($template.data("iconWarn"));
    } else if (this.severity === SeverityToId.info) {
      severityClass = parseString($template.data("iconInfo"));
    }
    const date = new Date(this.time);
    const hours = padStart(String(date.getHours()), 2, "0");
    const minutes = padStart(String(date.getMinutes()), 2, "0");
    const formattedTime = `${hours}:${minutes}`;
    $html.addClass(`fc-nh-severity-${idToSeverity(this.severity)}`);
    $html.removeClass("Hidden");
    $html.removeClass("fc-nh-template");
    $html.find(".fc-nh-severity").addClass(severityClass);
    setText($html.find(".fc-nh-time"), formattedTime);
    setText($html.find(".fc-nh-summary"), this.summary);
    setText($html.find(".fc-nh-detail"), this.detail);
    setText($html.find(".fc-nh-origin"), this.origin, (x) => (x !== "" ? `(${x})` : ""));
    return $html;
  }

  toJSON(): Record<string, string | number> {
    return {
      details: this.detail,
      origin: this.origin,
      severity: this.severity,
      summary: this.summary,
      time: this.time,
    };
  }

  toString(pretty = false): string {
    return JSON.stringify(this.toJSON(), undefined, pretty ? 2 : undefined);
  }

  /**
   * @param item The growl item with the message.
   * @returns A new notification model.
   */
  static forGrowlItem(item: TElementSpecifier<HTMLElement>): Notification {
    const $item = toJQuery(item);
    const $message = $item.find(".ui-growl-message");
    const $children = $message.contents().filter((_index, node) => node.nodeType !== Node.COMMENT_NODE);
    const $image = $item.find(".ui-growl-image");
    const summary = $children.first().text();
    const detail = $children.last().text();
    let severity: TSeverityId;
    if ($image.hasClass("ui-growl-image-info")) {
      severity = SeverityToId.info;
    } else if ($image.hasClass("ui-growl-image-warn")) {
      severity = SeverityToId.warn;
    } else if ($image.hasClass("ui-growl-image-error")) {
      severity = SeverityToId.error;
    } else if ($image.hasClass("ui-growl-image-fatal")) {
      severity = SeverityToId.fatal;
    } else {
      console.warn("Could not determined severity of notification", item);
      severity = SeverityToId.info;
    }
    return new Notification(summary, detail, severity, "", Date.now());
  }

  /**
   * @param message The growl message.
   * @returns The newly created notification.
   */
  static forGrowlMessage(message: PrimeFaces.FacesMessage): Notification {
    let severity: TSeverityId;
    switch (message.severity) {
      case "error":
        severity = SeverityToId.error;
        break;
      case "fatal":
        severity = SeverityToId.fatal;
        break;
      case "info":
        severity = SeverityToId.info;
        break;
      case "warn":
        severity = SeverityToId.warn;
        break;
      default:
        assertNever(message.severity);
    }
    return new Notification(message.summary ?? "", message.detail ?? "", severity, "", Date.now());
  }

  /**
   * @param serialized The serialized notification as return by toJSON().
   * @returns The parsed notification.
   */
  static forJSON(serialized: Record<string, unknown>): Notification {
    const severity = parseNumber(serialized.severity);
    return new Notification(
      parseString(serialized.summary),
      parseString(serialized.detail),
      isSeverityId(severity) ? severity : SeverityToId.error,
      parseString(serialized.origin),
      typeof serialized.time === "number" ? serialized.time : Date.now(),
    );
  }

  /**
   * @param serialized The serialized notification as returned by toString().
   * @returns The deserialized notification.
   */
  static forString(serialized: string): Notification {
    return Notification.forJSON(parseJsonObject(serialized));
  }
}

/**
 * @member {Notification[]} notifications List of all known notifications.
 * @member {Map<HTMLElement,Notification>} domMap between the original DOM element and the notification that was extracted. Used prevent duplicate messages.
 * @member {Set<Severity>} severities All severities that have occurred up until now.
 */
export class NotificationHistory {
  /** List of all known notifications. */
  private _notifications: Notification[];

  /** All severities that have occurred up until now. */
  private _severities: Set<number>;

  constructor() {
    this._severities = new Set<number>();
    this._notifications = [];
  }

  /**
   * @param notification Notification to add.
   */
  addNotification(notification: Notification): void {
    this._notifications.push(notification);
    this._severities.add(notification.severity);
    this._renderAll([notification]);
    this._checkTriggerVisibility();
  }

  private _checkTriggerVisibility() {
    const hasInfo = this._severities.has(SeverityToId.info);
    if (this._severities.size === 1 ? !hasInfo : this._severities.size > 1) {
      $(".fc-notification-history-trigger").removeClass("fc-nh-newview");
    }
  }

  private _cleanAll() {
    const $list = $(".fc-nh-list");
    const $item = $list.children(".fc-nh-entry").not(".fc-nh-template");
    $item.remove();
  }

  private _loadFromSession(): void {
    const notifications = $.map(getSessionMessages(), (json) => Notification.forJSON(json));
    this._notifications = this._notifications.concat(notifications);
    this._notifications.sort((lhs, rhs) => rhs.time - lhs.time);
    for (const notification of this._notifications) {
      this._severities.add(notification.severity);
    }
    this._cleanAll();
    this._renderAll(this._notifications);
  }

  /**
   * @param notifications List of notifications to render.
   */
  private _renderAll(notifications: Notification[]) {
    const $list = $(".fc-nh-list");
    if (notifications.length > 0) {
      const $count = $(".fc-nh-count");
      const $template = $list.children(".fc-nh-template");
      const $rendered = $.map(notifications, (notification) => notification.render($template));
      $list.prepend($rendered);
      $count.text(this._notifications.length);
    }

    // Show or hide the message for when no notifcations are available
    changeStyleClass($(".fc-nh-none"), "Hidden", $list.children().length > 1);

    // Do not hide the close button of dialogs
    const growl = findWidgetForId($(".fc-global-growl").attr("id") ?? "", PrimeFaces.widget.Growl);

    if (growl !== undefined) {
      if ($(".ui-dialog").not(".fc-loading-dialog").filter(":visible").length > 0) {
        growl.getJQ().css({ top: "124px" });
      } else {
        growl.getJQ().css({ top: "73px" });
      }
    }
  }

  /**
   * @returns The notification history singleton for the current page.
   */
  static getInstance(): NotificationHistory {
    if (NotificationInstance === undefined) {
      NotificationInstance = new NotificationHistory();
      NotificationInstance._loadFromSession();
    }
    return NotificationInstance;
  }
}
